/* ___eslint-disable jsdoc/no-types */
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  skipValidation: true, // Skip validation for now
  server: {
    // Vercel
    VERCEL_URL: z.string().min(1).optional(),
    NEXT_RUNTIME: z.enum(['edge', 'nodejs']).optional(),
    VERCEL_ENV: z.enum(['development', 'preview', 'production']).optional(),

    // Environment
    PORT: z.number().optional(),
    DOMAIN: z.string().min(1).optional(),
    NODE_ENV: z.enum(['development', 'production']).optional(),
    LOG_API_REQUESTS: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
    ANALYZE: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
  },
  client: {
    // App Config
    NEXT_PUBLIC_APP_URL: z.string().min(1),
    NEXT_PUBLIC_APP_NAME: z.string().min(1),
    NEXT_PUBLIC_ENVIRONMENT: z.enum(['local', 'development', 'staging', 'production', 'testing']),

    // Monitoring
    NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID: z.string().min(1),
    NEXT_PUBLIC_HIGHLIGHT_SERVICE_NAME: z.string().min(1),
    NEXT_PUBLIC_METICULOUS_PROJECT_ID: z.string().min(1),

    // Feature Gates
    NEXT_PUBLIC_FEATURE_ADMIN_USER_LIFECYCLE_ACTIONS: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
    NEXT_PUBLIC_FEATURE_ADMIN_USER_MFA_RESET: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
    NEXT_PUBLIC_FEATURE_FORM_REQUIRED_MARKERS: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => (value == null ? undefined : value === 'true')),
    NEXT_PUBLIC_FEATURE_FORM_RESERVED_ERROR_SPACE: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
  },
  runtimeEnv: {
    // ======================================== Server ======================================== //
    // Vercel
    VERCEL_URL: process.env.VERCEL_URL,
    VERCEL_ENV: process.env.VERCEL_ENV,
    NEXT_RUNTIME: process.env.NEXT_RUNTIME,

    // Environment
    PORT: process.env.PORT,
    ANALYZE: process.env.ANALYZE,
    NODE_ENV: process.env.NODE_ENV,
    DOMAIN: process.env.DOMAIN,
    LOG_API_REQUESTS: process.env.LOG_API_REQUESTS,

    // ======================================== Client ======================================== //
    // App Config
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,

    // Monitoring
    NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID: process.env.NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID,
    NEXT_PUBLIC_HIGHLIGHT_SERVICE_NAME: process.env.NEXT_PUBLIC_HIGHLIGHT_SERVICE_NAME,
    NEXT_PUBLIC_METICULOUS_PROJECT_ID: process.env.NEXT_PUBLIC_METICULOUS_PROJECT_ID,

    // Feature Gates
    NEXT_PUBLIC_FEATURE_ADMIN_USER_LIFECYCLE_ACTIONS: process.env.NEXT_PUBLIC_FEATURE_ADMIN_USER_LIFECYCLE_ACTIONS,
    NEXT_PUBLIC_FEATURE_ADMIN_USER_MFA_RESET: process.env.NEXT_PUBLIC_FEATURE_ADMIN_USER_MFA_RESET,
    NEXT_PUBLIC_FEATURE_FORM_REQUIRED_MARKERS: process.env.NEXT_PUBLIC_FEATURE_FORM_REQUIRED_MARKERS,
    NEXT_PUBLIC_FEATURE_FORM_RESERVED_ERROR_SPACE: process.env.NEXT_PUBLIC_FEATURE_FORM_RESERVED_ERROR_SPACE,
  },
});

/**
 * Get an environment variable.
 *
 * @template {keyof typeof env} T
 * @param {T} key - The key of the environment variable.
 * @param {typeof env[T]} defaultValue - The default value if the environment variable is not set.
 * @returns {typeof env[T]} The value of the environment variable or the default value.
 */
export const getEnv = (key, defaultValue) => env[key] ?? defaultValue;

const domain = getEnv('DOMAIN', '');
const currentEnv = getEnv('NEXT_PUBLIC_ENVIRONMENT', 'development');
const localDomain = domain || 'local.sportsfi.tech';

/** @type {Record<typeof currentEnv, string>} */
const environmentUrls = {
  local: `https://club.${localDomain}`,
  production: `https://${domain}`,
  staging: `https://staging.${domain}`,
  testing: `https://testing.${domain}`,
  development: `https://club.${localDomain}`,
};

export const isTest = () => currentEnv === 'testing';
export const isDev = () => currentEnv === 'development';
export const isStaging = () => currentEnv === 'staging';
export const isProd = () => currentEnv === 'production';

/**
 * Get the URL for the current environment.
 *
 * @returns {typeof environmentUrls[keyof typeof environmentUrls]} The URL for the current environment.
 */
export const getEnvUrl = () => environmentUrls[currentEnv];

/* ___eslint-enable jsdoc/no-types */
